import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Avatar,
  Card,
  CardContent,
  Divider,
  Tabs,
  Tab,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  ManageAccounts as ManagerIcon,
  Edit as EditorIcon,
  Block as BlockIcon,
  CheckCircle as ActiveIcon
} from '@mui/icons-material';
import { userService } from '../../services/firebase';
import type { User } from '../../types';
import { Timestamp } from 'firebase/firestore';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-tabpanel-${index}`}
      aria-labelledby={`user-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ManageUsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [tabValue, setTabValue] = useState(0);

  // Dialog states
  const [editDialog, setEditDialog] = useState<{
    open: boolean;
    user: User | null;
  }>({ open: false, user: null });

  const [viewDialog, setViewDialog] = useState<{
    open: boolean;
    user: User | null;
  }>({ open: false, user: null });

  const [addDialog, setAddDialog] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, roleFilter, statusFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const usersData = await userService.getAllUsers();
      setUsers(usersData);
    } catch (err) {
      setError('Kullanıcılar yüklenirken hata oluştu');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = users;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phoneNumber?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    // Status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'verified') {
        filtered = filtered.filter(user => user.isVerified);
      } else if (statusFilter === 'unverified') {
        filtered = filtered.filter(user => !user.isVerified);
      }
    }

    setFilteredUsers(filtered);
  };

  const handleUpdateUser = async (uid: string, updates: Partial<User>) => {
    try {
      setSaving(true);
      await userService.updateUser(uid, updates);
      
      // Update local state
      setUsers(prevUsers =>
        prevUsers.map(user =>
          user.uid === uid ? { ...user, ...updates } : user
        )
      );
      
      setSuccessMessage('Kullanıcı başarıyla güncellendi');
      setEditDialog({ open: false, user: null });
    } catch (err) {
      setError('Kullanıcı güncellenirken hata oluştu');
      console.error('Error updating user:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleUserStatus = async (uid: string, currentStatus: boolean) => {
    await handleUpdateUser(uid, { isVerified: !currentStatus });
  };

  const handleChangeUserRole = async (uid: string, newRole: 'admin' | 'manager' | 'editor' | 'user') => {
    await handleUpdateUser(uid, { role: newRole });
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <AdminIcon color="error" />;
      case 'manager':
        return <ManagerIcon color="warning" />;
      case 'editor':
        return <EditorIcon color="info" />;
      default:
        return <PersonIcon color="action" />;
    }
  };

  const getRoleColor = (role: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'manager':
        return 'warning';
      case 'editor':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDate = (timestamp: Timestamp | undefined) => {
    if (!timestamp) return 'Bilinmiyor';
    return timestamp.toDate().toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatsForTab = (tabIndex: number) => {
    switch (tabIndex) {
      case 0:
        return filteredUsers.length;
      case 1:
        return filteredUsers.filter(user => user.role === 'admin').length;
      case 2:
        return filteredUsers.filter(user => user.role === 'manager').length;
      case 3:
        return filteredUsers.filter(user => user.role === 'editor').length;
      case 4:
        return filteredUsers.filter(user => user.role === 'user').length;
      default:
        return 0;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Kullanıcı Yönetimi
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchUsers}
            disabled={loading}
          >
            Yenile
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialog(true)}
          >
            Yeni Kullanıcı
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
        <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Toplam Kullanıcı
                  </Typography>
                  <Typography variant="h4">
                    {users.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ActiveIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Doğrulanmış
                  </Typography>
                  <Typography variant="h4">
                    {users.filter(user => user.isVerified).length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AdminIcon sx={{ fontSize: 40, color: 'error.main', mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Yöneticiler
                  </Typography>
                  <Typography variant="h4">
                    {users.filter(user => user.role === 'admin').length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BlockIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Doğrulanmamış
                  </Typography>
                  <Typography variant="h4">
                    {users.filter(user => !user.isVerified).length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
              <TextField
                fullWidth
                label="Kullanıcı Ara"
                variant="outlined"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
                }}
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
              <FormControl fullWidth>
                <InputLabel>Rol Filtresi</InputLabel>
                <Select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                >
                  <MenuItem value="all">Tüm Roller</MenuItem>
                  <MenuItem value="admin">Yönetici</MenuItem>
                  <MenuItem value="manager">Müdür</MenuItem>
                  <MenuItem value="editor">Editör</MenuItem>
                  <MenuItem value="user">Kullanıcı</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
              <FormControl fullWidth>
                <InputLabel>Durum Filtresi</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">Tüm Durumlar</MenuItem>
                  <MenuItem value="verified">Doğrulanmış</MenuItem>
                  <MenuItem value="unverified">Doğrulanmamış</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label={`Tümü (${getStatsForTab(0)})`} />
          <Tab label={`Yöneticiler (${getStatsForTab(1)})`} />
          <Tab label={`Müdürler (${getStatsForTab(2)})`} />
          <Tab label={`Editörler (${getStatsForTab(3)})`} />
          <Tab label={`Kullanıcılar (${getStatsForTab(4)})`} />
        </Tabs>
      </Box>

      {/* Users Table */}
      <TabPanel value={tabValue} index={0}>
        <UsersTable
          users={filteredUsers}
          onEdit={(user) => setEditDialog({ open: true, user })}
          onView={(user) => setViewDialog({ open: true, user })}
          onToggleStatus={handleToggleUserStatus}
          onChangeRole={handleChangeUserRole}
          getRoleIcon={getRoleIcon}
          getRoleColor={getRoleColor}
          formatDate={formatDate}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <UsersTable
          users={filteredUsers.filter(user => user.role === 'admin')}
          onEdit={(user) => setEditDialog({ open: true, user })}
          onView={(user) => setViewDialog({ open: true, user })}
          onToggleStatus={handleToggleUserStatus}
          onChangeRole={handleChangeUserRole}
          getRoleIcon={getRoleIcon}
          getRoleColor={getRoleColor}
          formatDate={formatDate}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <UsersTable
          users={filteredUsers.filter(user => user.role === 'manager')}
          onEdit={(user) => setEditDialog({ open: true, user })}
          onView={(user) => setViewDialog({ open: true, user })}
          onToggleStatus={handleToggleUserStatus}
          onChangeRole={handleChangeUserRole}
          getRoleIcon={getRoleIcon}
          getRoleColor={getRoleColor}
          formatDate={formatDate}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <UsersTable
          users={filteredUsers.filter(user => user.role === 'editor')}
          onEdit={(user) => setEditDialog({ open: true, user })}
          onView={(user) => setViewDialog({ open: true, user })}
          onToggleStatus={handleToggleUserStatus}
          onChangeRole={handleChangeUserRole}
          getRoleIcon={getRoleIcon}
          getRoleColor={getRoleColor}
          formatDate={formatDate}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <UsersTable
          users={filteredUsers.filter(user => user.role === 'user')}
          onEdit={(user) => setEditDialog({ open: true, user })}
          onView={(user) => setViewDialog({ open: true, user })}
          onToggleStatus={handleToggleUserStatus}
          onChangeRole={handleChangeUserRole}
          getRoleIcon={getRoleIcon}
          getRoleColor={getRoleColor}
          formatDate={formatDate}
        />
      </TabPanel>

      {/* Edit User Dialog */}
      <EditUserDialog
        open={editDialog.open}
        user={editDialog.user}
        onClose={() => setEditDialog({ open: false, user: null })}
        onSave={handleUpdateUser}
        saving={saving}
      />

      {/* View User Dialog */}
      <ViewUserDialog
        open={viewDialog.open}
        user={viewDialog.user}
        onClose={() => setViewDialog({ open: false, user: null })}
        formatDate={formatDate}
        getRoleIcon={getRoleIcon}
      />

      {/* Add User Dialog */}
      <AddUserDialog
        open={addDialog}
        onClose={() => setAddDialog(false)}
        onSuccess={() => {
          setAddDialog(false);
          fetchUsers();
          setSuccessMessage('Kullanıcı başarıyla eklendi');
        }}
      />
    </Container>
  );
};

// Users Table Component
const UsersTable: React.FC<{
  users: User[];
  onEdit: (user: User) => void;
  onView: (user: User) => void;
  onToggleStatus: (uid: string, currentStatus: boolean) => void;
  onChangeRole: (uid: string, newRole: 'admin' | 'manager' | 'editor' | 'user') => void;
  getRoleIcon: (role: string) => React.ReactElement;
  getRoleColor: (role: string) => "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning";
  formatDate: (timestamp: Timestamp | undefined) => string;
}> = ({ users, onEdit, onView, onToggleStatus, onChangeRole, getRoleIcon, getRoleColor, formatDate }) => {
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Kullanıcı</TableCell>
            <TableCell>İletişim</TableCell>
            <TableCell>Rol</TableCell>
            <TableCell>Durum</TableCell>
            <TableCell>Son Giriş</TableCell>
            <TableCell>Kayıt Tarihi</TableCell>
            <TableCell>İşlemler</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.uid}>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar 
                    src={user.avatar} 
                    sx={{ mr: 2, width: 40, height: 40 }}
                  >
                    {user.displayName?.charAt(0) || user.email?.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2">
                      {user.displayName || 'İsimsiz'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {user.bio || 'Bio yok'}
                    </Typography>
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{user.email}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {user.phoneNumber || 'Telefon yok'}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  icon={getRoleIcon(user.role)}
                  label={user.role}
                  color={getRoleColor(user.role)}
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Chip
                  label={user.isVerified ? 'Doğrulanmış' : 'Doğrulanmamış'}
                  color={user.isVerified ? 'success' : 'warning'}
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {formatDate(user.lastLoginAt)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {formatDate(user.createdAt)}
                </Typography>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => onView(user)}
                    title="Görüntüle"
                  >
                    <ViewIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => onEdit(user)}
                    title="Düzenle"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => onToggleStatus(user.uid, user.isVerified)}
                    color={user.isVerified ? 'warning' : 'success'}
                    title={user.isVerified ? 'Engelle' : 'Aktifleştir'}
                  >
                    {user.isVerified ? <BlockIcon /> : <ActiveIcon />}
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
          {users.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} align="center">
                <Typography variant="body2" color="textSecondary">
                  Kullanıcı bulunamadı
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Edit User Dialog Component
const EditUserDialog: React.FC<{
  open: boolean;
  user: User | null;
  onClose: () => void;
  onSave: (uid: string, updates: Partial<User>) => void;
  saving: boolean;
}> = ({ open, user, onClose, onSave, saving }) => {
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    phoneNumber: '',
    role: 'user' as 'admin' | 'manager' | 'editor' | 'user',
    bio: '',
    isVerified: false
  });

  useEffect(() => {
    if (user) {
      setFormData({
        displayName: user.displayName || '',
        email: user.email || '',
        phoneNumber: user.phoneNumber || '',
        role: user.role,
        bio: user.bio || '',
        isVerified: user.isVerified
      });
    }
  }, [user]);

  const handleSave = () => {
    if (user) {
      onSave(user.uid, formData);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Kullanıcı Düzenle</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Ad Soyad"
            value={formData.displayName}
            onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
            fullWidth
          />
          <TextField
            label="E-posta"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            fullWidth
            type="email"
          />
          <TextField
            label="Telefon"
            value={formData.phoneNumber}
            onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
            fullWidth
          />
          <TextField
            label="Bio"
            value={formData.bio}
            onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
            multiline
            rows={3}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>Rol</InputLabel>
            <Select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
            >
              <MenuItem value="user">Kullanıcı</MenuItem>
              <MenuItem value="editor">Editör</MenuItem>
              <MenuItem value="manager">Müdür</MenuItem>
              <MenuItem value="admin">Yönetici</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={formData.isVerified}
                onChange={(e) => setFormData({ ...formData, isVerified: e.target.checked })}
              />
            }
            label="Doğrulanmış"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          İptal
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          Kaydet
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// View User Dialog Component
const ViewUserDialog: React.FC<{
  open: boolean;
  user: User | null;
  onClose: () => void;
  formatDate: (timestamp: Timestamp | undefined) => string;
  getRoleIcon: (role: string) => React.ReactElement;
}> = ({ open, user, onClose, formatDate, getRoleIcon }) => {
  if (!user) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar src={user.avatar} sx={{ width: 56, height: 56 }}>
            {user.displayName?.charAt(0) || user.email?.charAt(0)}
          </Avatar>
          <Box>
            <Typography variant="h6">{user.displayName || 'İsimsiz'}</Typography>
            <Typography variant="body2" color="textSecondary">
              {user.email}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          <Box sx={{ flex: '1 1 400px', minWidth: 400 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Kişisel Bilgiler
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography><strong>Ad Soyad:</strong> {user.displayName || 'Belirtilmemiş'}</Typography>
                  <Typography><strong>E-posta:</strong> {user.email}</Typography>
                  <Typography><strong>Telefon:</strong> {user.phoneNumber || 'Belirtilmemiş'}</Typography>
                  <Typography><strong>Bio:</strong> {user.bio || 'Bio yok'}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 400px', minWidth: 400 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Hesap Bilgileri
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <strong>Rol:</strong>
                    {getRoleIcon(user.role)}
                    <Chip label={user.role} size="small" />
                  </Box>
                  <Typography>
                    <strong>Durum:</strong>
                    <Chip
                      label={user.isVerified ? 'Doğrulanmış' : 'Doğrulanmamış'}
                      color={user.isVerified ? 'success' : 'warning'}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                  <Typography><strong>Kayıt Tarihi:</strong> {formatDate(user.createdAt)}</Typography>
                  <Typography><strong>Son Güncelleme:</strong> {formatDate(user.updatedAt)}</Typography>
                  <Typography><strong>Son Giriş:</strong> {formatDate(user.lastLoginAt)}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
};

// Add User Dialog Component (Placeholder)
const AddUserDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ open, onClose, onSuccess }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Yeni Kullanıcı Ekle</DialogTitle>
      <DialogContent>
        <Alert severity="info">
          Yeni kullanıcı ekleme özelliği yakında eklenecek. Şu anda kullanıcılar sadece kayıt olarak sisteme dahil olabilirler.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageUsersPage; 